// ==================== PAYMENT TRACKING SERVICE ====================
// Extracted from database.js - contains all payment tracking functionality

import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../../utils/database/firebase-config';
// Import related services
import { invoicesService } from '../accounting/invoices';
import { journalEntriesService } from '../accounting/journal-entries';
import { auditService, AUDIT_MODULES, AUDIT_ACTIONS, AUDIT_TAGS } from '../system/audit';

const paymentLabel = (p) => p?.paymentNumber || p?.reference || p?.id || 'payment';

export const paymentTrackingService = {
  async getPayments(companyId) {
    const paymentsRef = collection(db, 'payments');
    const q = query(paymentsRef, where('companyId', '==', companyId), orderBy('paymentDate', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  async addPayment(paymentData) {
    const paymentsRef = collection(db, 'payments');
    
    // Get invoice and customer information for better tracking
    let enrichedPaymentData = { ...paymentData };
    if (paymentData.invoiceId) {
      try {
        const invoice = await invoicesService.getInvoice(paymentData.invoiceId);
        if (invoice) {
          enrichedPaymentData.invoiceNumber = invoice.invoiceNumber || invoice.id;
          enrichedPaymentData.customerId = invoice.clientId;
          
          // Try multiple fallback methods for customer name
          enrichedPaymentData.customerName = 
            invoice.client?.name || 
            invoice.client?.companyName ||
            invoice.clientName || 
            invoice.customerName ||
            paymentData.customerName ||
            'Unknown Customer';
        }
      } catch (error) {
        console.error('Error fetching invoice details for payment:', error);
        // Fallback to payment data if invoice fetch fails
        enrichedPaymentData.customerName = paymentData.customerName || 'Unknown Customer';
      }
    } else {
      // If no invoiceId, ensure we have a customer name
      enrichedPaymentData.customerName = paymentData.customerName || 'Unknown Customer';
    }
    
    // Clean the data to remove any undefined values before saving to Firestore
    const cleanedPaymentData = Object.fromEntries(
      Object.entries(enrichedPaymentData).filter(([_, value]) => value !== undefined)
    );
    
    const docRef = await addDoc(paymentsRef, {
      ...cleanedPaymentData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      status: 'recorded'
    });

    // Update invoice payment status if invoiceId is provided
    if (paymentData.invoiceId) {
      await this.updateInvoicePaymentStatus(paymentData.invoiceId, paymentData.amount, 'partial');
    }

    // Create journal entry for the payment (check automation settings first)
    try {
      const { accountMappingService } = await import('../accounting/account-mapping');
      const isJournalAutomationEnabled = await accountMappingService.isAutomationEnabled(
        paymentData.companyId, 
        'paymentJournalEntries', 
        'journalEntry'
      );
      
      if (isJournalAutomationEnabled) {
        await this.createPaymentJournalEntry(paymentData.companyId, {
          ...enrichedPaymentData,
          id: docRef.id
        }, paymentData.createdBy);
      } else {
        console.log('Payment journal entry automation is disabled');
      }
    } catch (error) {
      console.error('Error creating payment journal entry:', error);
    }

    auditService.log({
      module: AUDIT_MODULES.PAYMENT,
      action: AUDIT_ACTIONS.PAYMENT_RECORDED,
      recordId: docRef.id,
      recordLabel: paymentLabel(cleanedPaymentData),
      after: cleanedPaymentData,
      tags: [AUDIT_TAGS.ALL, AUDIT_TAGS.FINANCIAL],
      companyId: paymentData.companyId,
      meta: {
        paymentNumber: cleanedPaymentData.paymentNumber || docRef.id,
        invoiceNumber: cleanedPaymentData.invoiceNumber,
        customerId: cleanedPaymentData.customerId,
        amount: cleanedPaymentData.amount
      }
    });

    return docRef.id;
  },

  async updatePayment(paymentId, paymentData) {
    const paymentRef = doc(db, 'payments', paymentId);
    let before = null;
    try {
      const snap = await getDoc(paymentRef);
      if (snap.exists()) before = { id: snap.id, ...snap.data() };
    } catch { /* best effort */ }

    await updateDoc(paymentRef, {
      ...paymentData,
      updatedAt: serverTimestamp()
    });

    auditService.logUpdate({
      module: AUDIT_MODULES.PAYMENT,
      recordId: paymentId,
      recordLabel: paymentLabel({ ...before, ...paymentData }),
      before,
      after: { ...(before || {}), ...paymentData },
      extraTags: [AUDIT_TAGS.FINANCIAL],
      meta: {
        paymentNumber: before?.paymentNumber || paymentId,
        invoiceNumber: before?.invoiceNumber,
        customerId: before?.customerId
      }
    });
    return true;
  },

  async deletePayment(paymentId) {
    const paymentRef = doc(db, 'payments', paymentId);
    let before = null;
    try {
      const snap = await getDoc(paymentRef);
      if (snap.exists()) before = { id: snap.id, ...snap.data() };
    } catch { /* best effort */ }

    await deleteDoc(paymentRef);

    auditService.log({
      module: AUDIT_MODULES.PAYMENT,
      action: AUDIT_ACTIONS.PAYMENT_DELETED,
      recordId: paymentId,
      recordLabel: paymentLabel(before),
      before,
      after: null,
      tags: [AUDIT_TAGS.ALL, AUDIT_TAGS.FINANCIAL, AUDIT_TAGS.DELETIONS],
      companyId: before?.companyId,
      meta: {
        paymentNumber: before?.paymentNumber || paymentId,
        invoiceNumber: before?.invoiceNumber,
        customerId: before?.customerId,
        amount: before?.amount
      }
    });
    return true;
  },

  async updateInvoicePaymentStatus(invoiceId, paidAmount, status = 'partial') {
    // First get the invoice to find the correct document ID
    const invoice = await invoicesService.getInvoice(invoiceId);
    
    if (!invoice) {
      throw new Error('Invoice not found');
    }
    
    // Use the document ID (docId) for the update, not the formatted invoice number
    const documentId = invoice.docId || invoiceId;
    const invoiceRef = doc(db, 'invoices', documentId);
    const invoiceDoc = await getDoc(invoiceRef);
    
    if (!invoiceDoc.exists()) {
      throw new Error('Invoice document not found');
    }

    const invoiceData = invoiceDoc.data();
    const totalAmount = invoiceData.finalTotal || invoiceData.total || 0;
    const currentPaid = invoiceData.paidAmount || 0;
    const newPaidAmount = currentPaid + paidAmount;

    let newStatus = status;
    if (newPaidAmount >= totalAmount) {
      newStatus = 'paid';
    } else if (newPaidAmount > 0) {
      newStatus = 'partial';
    } else {
      newStatus = 'unpaid';
    }

    // Update the invoice using the correct document ID
    await updateDoc(invoiceRef, {
      paidAmount: newPaidAmount,
      paymentStatus: newStatus,
      status: newStatus,
      lastPaymentDate: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    // Update related shipments' payment status
    if (invoiceData.shipments && invoiceData.shipments.length > 0) {
      const shipmentUpdates = invoiceData.shipments.map(async (shipment) => {
        if (shipment.id) {
          const shipmentRef = doc(db, 'shipments', shipment.id);
          await updateDoc(shipmentRef, {
            paymentStatus: newStatus,
            isPaid: newStatus === 'paid',
            lastPaymentDate: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        }
      });
      await Promise.all(shipmentUpdates);
    }

    return { newPaidAmount, newStatus };
  },

  async createPaymentJournalEntry(companyId, payment, createdBy) {
    const { accountMappingService } = await import('../accounting/account-mapping');
    
    // Get automation settings for account mappings
    const automationSettings = await accountMappingService.getAutomationSettings(companyId);
    const paymentSettings = automationSettings.paymentJournalEntries || {};
    
    const paymentMethod = payment.paymentMethod || 'cash';
    let cashAccountCode = paymentSettings.cashAccountMapping || '1000'; // Default cash account
    
    // Map payment methods to appropriate accounts based on settings
    if (paymentMethod === 'bank_transfer' || paymentMethod === 'check' || paymentMethod === 'credit_card') {
      cashAccountCode = paymentSettings.bankAccountMapping || '1010'; // Bank account
    }

    const customerName = payment.customerName || payment.client?.name || 'Customer';
    const invoiceRef = payment.invoiceNumber || payment.invoiceId || 'N/A';
    const receivableAccountCode = paymentSettings.receivableAccountMapping || '1100';
    
    const lineItems = [
      {
        accountCode: cashAccountCode,
        accountName: payment.paymentMethod === 'cash' ? 'Cash' : 'Bank',
        description: `Payment received from ${customerName}`,
        debit: payment.amount,
        credit: 0
      },
      {
        accountCode: receivableAccountCode,
        accountName: 'Accounts Receivable',
        description: `Payment for invoice ${invoiceRef}`,
        debit: 0,
        credit: payment.amount
      }
    ];

    const entryData = {
      companyId,
      entryNumber: await journalEntriesService.generateEntryNumber(companyId),
      date: payment.paymentDate || new Date(),
      description: `Payment received - ${invoiceRef}`,
      reference: payment.reference || invoiceRef,
      sourceType: 'payment',
      sourceId: payment.id,
      lineItems,
      status: 'draft',
      createdBy,
      metadata: {
        paymentMethod: payment.paymentMethod || 'cash',
        customerName: customerName,
        invoiceId: payment.invoiceId
      }
    };

    const journalId = await journalEntriesService.addJournalEntry(entryData);
    // Auto-post since it's a payment entry
    await journalEntriesService.postJournalEntry(journalId, createdBy);
    return journalId;
  },

  async getInvoicePaymentHistory(invoiceId) {
    const paymentsRef = collection(db, 'payments');
    const q = query(paymentsRef, where('invoiceId', '==', invoiceId), orderBy('paymentDate', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  async getCustomerPaymentHistory(companyId, customerId) {
    const paymentsRef = collection(db, 'payments');
    const q = query(paymentsRef, 
      where('companyId', '==', companyId),
      where('customerId', '==', customerId),
      orderBy('paymentDate', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  async generateAgingReport(companyId) {
    // TODO: Reconnect with invoicesService and clientsService after refactoring
    // const invoices = await invoicesService.getInvoices(companyId);
    // const clients = await clientsService.getClients(companyId);
    const invoices = []; // Temporary placeholder
    const clients = []; // Temporary placeholder
    
    const today = new Date();
    
    const agingBuckets = {
      current: { label: 'days 0-30', amount: 0, count: 0, invoices: [] },
      thirty: { label: 'days 31-60', amount: 0, count: 0, invoices: [] },
      sixty: { label: 'days 61-90', amount: 0, count: 0, invoices: [] },
      ninety: { label: 'days +90', amount: 0, count: 0, invoices: [] }
    };

    const clientsMap = new Map();
    
    // Create map with both Firestore document ID and custom clientId for lookup
    clients.forEach(client => {
      clientsMap.set(client.id, client); // Firestore document ID
      if (client.clientId) {
        clientsMap.set(client.clientId, client); // Custom client ID
      }
    });
    
    for (const invoice of invoices) {
      const paymentStatus = invoice.paymentStatus || 'unpaid';
      if (paymentStatus === 'paid') continue;

      const dueDate = invoice.dueDate?.toDate?.() || new Date(invoice.dueDate);
      const daysPastDue = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));
      const outstandingAmount = (invoice.finalTotal || invoice.total || 0) - (invoice.paidAmount || 0);

      // Skip if no outstanding amount
      if (outstandingAmount <= 0) continue;

      let bucket;
      if (daysPastDue <= 30) bucket = agingBuckets.current;
      else if (daysPastDue <= 60) bucket = agingBuckets.thirty;
      else if (daysPastDue <= 90) bucket = agingBuckets.sixty;
      else bucket = agingBuckets.ninety;

      bucket.amount += outstandingAmount;
      bucket.count += 1;
      
      // Populate client information with better fallback logic
      const client = clientsMap.get(invoice.clientId) || 
                    clientsMap.get(invoice.client?.id) ||
                    clientsMap.get(invoice.customerId);
      
      // Try multiple fallback methods for client name
      const clientName = client?.name || 
                        client?.companyName ||
                        invoice.client?.name || 
                        invoice.client?.companyName ||
                        invoice.clientName || 
                        invoice.customerName ||
                        invoice.shipments?.[0]?.clientName ||
                        'Unknown Customer';
      
      bucket.invoices.push({
        ...invoice,
        daysPastDue,
        outstandingAmount,
        client: client || { name: clientName },
        clientName: clientName,
        invoiceNumber: invoice.invoiceNumber || invoice.id || 'N/A'
      });
    }

    return {
      generated: serverTimestamp(),
      agingBuckets,
      totalOutstanding: Object.values(agingBuckets).reduce((sum, bucket) => sum + bucket.amount, 0),
      totalInvoices: Object.values(agingBuckets).reduce((sum, bucket) => sum + bucket.count, 0)
    };
  },

  async generateCollectionReport(companyId, startDate, endDate) {
    const paymentsRef = collection(db, 'payments');
    const q = query(paymentsRef,
      where('companyId', '==', companyId),
      where('paymentDate', '>=', startDate),
      where('paymentDate', '<=', endDate),
      orderBy('paymentDate', 'desc')
    );
    const snapshot = await getDocs(q);
    const payments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    const summary = {
      totalCollected: payments.reduce((sum, payment) => sum + (payment.amount || 0), 0),
      paymentCount: payments.length,
      averagePayment: payments.length > 0 ? payments.reduce((sum, payment) => sum + (payment.amount || 0), 0) / payments.length : 0,
      paymentMethods: {}
    };

    // Group by payment method
    payments.forEach(payment => {
      const method = payment.paymentMethod || 'cash';
      if (!summary.paymentMethods[method]) {
        summary.paymentMethods[method] = { amount: 0, count: 0 };
      }
      summary.paymentMethods[method].amount += payment.amount || 0;
      summary.paymentMethods[method].count += 1;
    });

    return {
      summary,
      payments,
      dateRange: { startDate, endDate }
    };
  },

  // Collection Tasks Management
  async getCollectionTasks(companyId) {
    const tasksRef = collection(db, 'collectionTasks');
    const q = query(tasksRef, where('companyId', '==', companyId), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  async createCollectionTask(taskData) {
    const tasksRef = collection(db, 'collectionTasks');
    const docRef = await addDoc(tasksRef, {
      ...taskData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      status: 'pending'
    });
    return docRef.id;
  },

  async completeCollectionTask(taskId, completedBy) {
    const taskRef = doc(db, 'collectionTasks', taskId);
    await updateDoc(taskRef, {
      status: 'completed',
      completedAt: serverTimestamp(),
      completedBy,
      updatedAt: serverTimestamp()
    });
    return true;
  },

  async updateCollectionTask(taskId, taskData) {
    const taskRef = doc(db, 'collectionTasks', taskId);
    await updateDoc(taskRef, {
      ...taskData,
      updatedAt: serverTimestamp()
    });
    return true;
  },

  async deleteCollectionTask(taskId) {
    const taskRef = doc(db, 'collectionTasks', taskId);
    await deleteDoc(taskRef);
    return true;
  },

  // Payment Recording (alias for addPayment for consistency)
  async recordPayment(paymentData) {
    return await this.addPayment(paymentData);
  },

  // Payment Reminder System
  async sendPaymentReminder(invoiceId, sentBy) {
    const reminderData = {
      invoiceId,
      sentBy,
      sentAt: serverTimestamp(),
      reminderType: 'payment_due',
      status: 'sent'
    };

    const remindersRef = collection(db, 'paymentReminders');
    const docRef = await addDoc(remindersRef, reminderData);

    // Update invoice with last reminder date
    const invoiceRef = doc(db, 'invoices', invoiceId);
    await updateDoc(invoiceRef, {
      lastReminderSent: serverTimestamp(),
      reminderCount: 1, // This should be incremented properly
      updatedAt: serverTimestamp()
    });

    return docRef.id;
  },

  async getPaymentReminders(companyId, invoiceId = null) {
    const remindersRef = collection(db, 'paymentReminders');
    let q;
    
    if (invoiceId) {
      q = query(remindersRef, where('invoiceId', '==', invoiceId), orderBy('sentAt', 'desc'));
    } else {
      // Get all reminders for invoices of this company
      // TODO: Reconnect with invoicesService after refactoring
      // const invoices = await invoicesService.getInvoices(companyId);
      const invoices = []; // Temporary placeholder
      const invoiceIds = invoices.map(inv => inv.id);
      
      if (invoiceIds.length === 0) return [];
      
      q = query(remindersRef, where('invoiceId', 'in', invoiceIds.slice(0, 10)), orderBy('sentAt', 'desc'));
    }
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  }
}; 